import { Collapse, Group, Select, Stack, TextInput } from '@mantine/core';
import { useCompanyConfiguration } from 'init/use-company-configuration';
import { i18nKeys, useTranslation } from 'locales';
import { useParams } from 'react-router';
import { isNullish } from 'remeda';
import { useLoadTemplateV2 } from 'shared/hooks/use-load-template-v2';
import {
  draftKey,
  type SendingOptionsDraft,
  useActiveChannelAtom,
  useSendingOptionsDraftsAtom,
  useTemplateLanguageAtom,
} from './state';
import { TemplateSubjectInput } from './TemplateSubjectInput';

export const TemplateEditorSendingOptions = () => {
  const { templateId } = useParams<{ templateId: string }>();
  const { template } = useLoadTemplateV2(templateId);
  const [activeChannel] = useActiveChannelAtom();

  const isEmailChannel = activeChannel === 'email';
  const hasEmailBodies = !isNullish(template) && template.bodies.email.length > 0;

  return (
    <Collapse expanded={isEmailChannel && hasEmailBodies}>
      {!isNullish(template) && <SendingOptionsFields templateId={template.id} />}
    </Collapse>
  );
};

const SendingOptionsFields = ({ templateId }: { templateId: string }) => {
  const { t } = useTranslation();
  const { template } = useLoadTemplateV2(templateId);
  const [language] = useTemplateLanguageAtom();
  const [drafts, setDrafts] = useSendingOptionsDraftsAtom();
  const company = useCompanyConfiguration();

  const bodyKey = draftKey(templateId, 'email', language ?? '');
  const body = template?.bodies.email.find(({ locale }) => locale === language);
  const draft = drafts[bodyKey];

  const fieldValue = (field: keyof SendingOptionsDraft) => draft?.[field] ?? body?.[field] ?? '';

  const setField = (field: keyof SendingOptionsDraft) => (value: string) =>
    setDrafts(previous => ({
      ...previous,
      [bodyKey]: { ...previous[bodyKey], [field]: value },
    }));

  const hasCustomDomainConfig = company.default_from && company.custom_domain;
  const customDomainAddress =
    hasCustomDomainConfig && `${company.default_from}@${company.custom_domain}`;
  const defaultEmail = customDomainAddress || 'no-reply@email-recovr.eu';

  const fromEmailItems = [
    {
      value: 'default',
      label: `${t(i18nKeys.TEMPLATE.SENDING_OPTIONS.DEFAULT_ADDRESS)} (${defaultEmail})`,
    },
    ...(hasCustomDomainConfig
      ? [
          { value: 'no-reply@email-recovr.eu', label: 'no-reply@email-recovr.eu' },
          ...(customDomainAddress
            ? [{ value: customDomainAddress, label: customDomainAddress }]
            : []),
        ]
      : []),
  ];

  return (
    <Stack
      gap="xs"
      p="md"
      bg="gray.0"
      style={{ borderBottom: '1px solid var(--mantine-color-gray-3)' }}
    >
      {template && (
        <TemplateSubjectInput
          label={t(i18nKeys.TEMPLATE.SENDING_OPTIONS.SUBJECT)}
          value={fieldValue('subject')}
          onChange={setField('subject')}
          planType={template.planType}
        />
      )}
      <Group grow align="flex-end" gap="xs">
        <TextInput
          label={t(i18nKeys.TEMPLATE.SENDING_OPTIONS.FROM)}
          placeholder={t(i18nKeys.NAME)}
          value={fieldValue('fromName')}
          onChange={event => setField('fromName')(event.currentTarget.value)}
        />
        <Select
          data={fromEmailItems}
          value={fieldValue('fromEmail') || 'default'}
          onChange={value => setField('fromEmail')(value ?? 'default')}
          disabled={!hasCustomDomainConfig}
          allowDeselect={false}
          comboboxProps={{ withinPortal: true }}
        />
      </Group>
      <Group grow align="flex-end" gap="xs">
        <TextInput
          label={t(i18nKeys.TEMPLATE.SENDING_OPTIONS.REPLY_TO)}
          placeholder={t(i18nKeys.NAME)}
          value={fieldValue('replyToName')}
          onChange={event => setField('replyToName')(event.currentTarget.value)}
        />
        <TextInput
          placeholder={t(i18nKeys.EMAIL)}
          value={fieldValue('replyToEmail')}
          onChange={event => setField('replyToEmail')(event.currentTarget.value)}
        />
      </Group>
    </Stack>
  );
};
